import { TypedUseLazyQuery } from "@reduxjs/toolkit/dist/query/react"
import dayjs from "dayjs"

import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import PerformanceTestStatus from "types/enums/PerformanceTestStatus"

import { getRoundedTemperature } from "utils/getRoundedTemperature"
import ContractorCompanyName from "components/table/columnComponents/ContractorCompanyName"
import PerformanceTestScheduleDate from "components/table/columnComponents/PerformaceTestScheduleDate"
import { TempChangeOfPerformanceTest } from "components/table/columnComponents/TempChangeOfPerformanceTest"

import downloadFile from "../utils/downloadFile"

interface Props {
  params: UITypes.Params
  headerCells: UITypes.TableHeaderCell[]
  meta?: ApiTypes.Model.Pagination | undefined
  fileName: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  convertData: (data: any) => UITypes.TableBodyRow[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useLazyQuery: TypedUseLazyQuery<any, any, any>
}

const useExportCSV = ({ params, headerCells, meta, fileName, convertData, useLazyQuery }: Props) => {
  const userTemperatureScale = useAppSelector(state => state.user.userTemperatureScale)
  const temperatureMark = userTemperatureScale === "celsius" ? "C" : "F"
  const [triggerQuery] = useLazyQuery()

  const getPerfTestScheduleDate = (item: UITypes.RowSettings["item"]) => {
    if ([PerformanceTestStatus.SCHEDULED, PerformanceTestStatus.RESCHEDULED].includes(item.perftest.status)) {
      if (item.perftest.schedule) {
        const dateTime = dayjs(item.perftest.schedule)
        return `${dateTime.format("DD MMM, YYYY")}, ${dateTime.format("hh:mm A")}`
      }

      const dateTime = dayjs(item.perftest.time)
      const date = dateTime < dateTime.hour(12) ? dateTime : dateTime.add(1, "day")

      return `${date.format("DD MMM, YYYY")}, 10:00 AM - 12:00 PM`
    }

    return ""
  }

  const getRowValues = (cell: UITypes.TableBodyRow, headerCells: UITypes.TableHeaderCell[]) => {
    return headerCells.map(headerCell => {
      const value = cell[headerCell.name]?.value || ""
      if (headerCell.type === "select") {
        return headerCell.options?.find(option => option.value === value)?.label || value
      }

      if (headerCell.cellComponent === PerformanceTestScheduleDate) {
        const item = cell.rowSettings.item
        return getPerfTestScheduleDate(item)
      }

      if (headerCell.cellComponent === ContractorCompanyName) {
        const item = cell.rowSettings.item
        return `${value}${item.parent_company ? ` (${item.parent_company.name})` : ""}`
      }

      if (headerCell.cellComponent === TempChangeOfPerformanceTest) {
        const item = cell.rowSettings.item

        if (item.perftest.start && item.perftest.end) {
          return `From ${getRoundedTemperature(item.perftest.start, 1)} ${temperatureMark} to ${getRoundedTemperature(item.perftest.end, 1)} ${temperatureMark}`
        }

        return ""
      }

      return value
    })
  }

  const getPerPage = () => {
    if (meta && meta.total < 100) {
      return meta.total
    }

    return 100
  }

  const triggerDataQuery = async () => {
    if (!meta) {
      const response = await triggerQuery({ ...params })
      return response.data
    }

    const total = meta?.total || 0
    const perPage = getPerPage()

    const pages = Math.ceil(total / perPage)

    const response = await Promise.all(
      Array.from({ length: pages }).map((_, i) => triggerQuery({ ...params, page: i + 1, per_page: perPage })),
    )

    return response.map(res => res.data.data).flat()
  }

  const handleExport = async () => {
    const data = await triggerDataQuery()

    const parsedData = convertData(data)
    const visibleHeaderCells = headerCells.filter(
      cell => cell.visibility !== false && cell.type !== "checkbox" && cell.type !== undefined,
    )
    const headerRow = visibleHeaderCells.map(cell => cell.label || cell.title)
    const CSVdata = [headerRow, ...parsedData.map(row => getRowValues(row, visibleHeaderCells))]

    downloadFile(
      [
        CSVdata.map(row =>
          row
            .map(item => (typeof item === "string" ? item.replaceAll('"', '""') : item))
            .map(item => (item?.toString().includes(",") ? `"${item}"` : item)),
        ).join("\n"),
      ],
      `${fileName}.csv`,
    )
  }

  return { handleExport }
}

export default useExportCSV
